import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../context/useAppContext';
import { Button, Form, RadioLabel, ToggleLabel, TooltipHelp, Modal, ToastContext } from '@weezevent/nacre';
import AddressForm from '../events/config/addressForm';
import { fetcher } from '@weezevent/weezjs-core';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';
import { usePermissions } from '../../user/permissions';

import Styles from '../../styles/eventCreation/eventCreation.module.css';
import { useSegment } from '../../components/tracking/trackingProvider';

export default function EventCreationLocalization({
    onChange,
    onChangeObject,
    onDone = null,
    formEvent,
    platformOptions = [],
    autoMode,
    disabledAutoMode = false,
    errors = {},
    setAutoMode = () => {},
    toggleFakeseating = false,
    isSubmitting,
    children,
    onCardDone = () => {},
    edit = false
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    const [{ organization, event }] = useAppContext();
    const [fakeseatingModal, setFakeseatingModal] = React.useState(false);
    const [fakeseating, setFakeseating] = React.useState(event?.isFakeSeating());
    const [disableFakeseating, setDisableFakeseating] = React.useState(event?.chart);
    const [isSubmittingFakeseating, setIsSubmittingFakeseating] = React.useState(false);
    const { addNotification } = useNotifications();
    const permissions = usePermissions();

    const resetForm = {
        address: {
            name: '',
            address1: '',
            address2: '',
            city: '',
            country: '',
            state: '',
            zip_code: ''
        },
        full_address: null,
        communication_platform: null,
        website: ''
    };

    const fieldErrors = React.useMemo(() => {
        if (typeof errors === 'object' && 'website' in errors) {
            return <Form.FieldErrors errors={[t(errors['website'])]} />;
        }
        return null;
    }, [errors, t]);

    const _onDone = React.useCallback(() => {
        onDone(onCardDone);
    }, [onCardDone, onDone]);

    const resetFakeseatingState = () => {
        setFakeseatingModal(false);
        setFakeseating(false);
    };

    // If the user come back to the page, we still need to block the toggle if the task is not over.
    React.useEffect(() => {
        const raw = window.localStorage.getItem('weez_notifications');
        const hasPendingNotification = raw ? JSON.parse(raw).some(n => n?.sub_type === 'generate_ensure_no_seated_conf') : false;

        if (hasPendingNotification) {
            setDisableFakeseating(true);
            setFakeseating(true);
        }
    }, []);

    const handleSubmit = React.useCallback(() => {
        setIsSubmittingFakeseating(true);

        const payload = {
            type: 'generate_ensure_no_seated_conf',
            resource_query_filter: {
                event__in: event.pk
            },
            config: {}
        };

        fetcher
            .post(`/ticket/organizations/${organization.id}/events/actions`, { data: payload })
            .then(response => {
                addNotification(
                    new Notification('async_actions', 'generate_ensure_no_seated_conf', 'events', response?.response?.task_id, {
                        success: 'ticket.creationEvent.localization.toasts.notification.success',
                        error: 'ticket.creationEvent.localization.toasts.notification.error'
                    })
                );
                toast.info(t('ticket.creationEvent.localization.toasts.async'));
                // We can't resync the event, so we're forcing the disabling of the toggle
                setDisableFakeseating(true);
                setFakeseatingModal(false);
            })
            .catch(e => {
                toast.error(t('ticket.common.toast_error'));
                resetFakeseatingState();
            })
            .finally(() => {
                setIsSubmittingFakeseating(false);
            });
    }, [event, organization, fetcher]);

    const ToggleFakeseatingEvent = () => (
        <div>
            {t('ticket.creationEvent.localization.toggle_event_fakeseating')}
            <TooltipHelp title={t('ticket.creationEvent.localization.tooltip_event_fakeseating')} />
        </div>
    );

    return (
        <>
            <Modal
                size="medium"
                open={fakeseatingModal}
                onClose={() => {
                    resetFakeseatingState();
                }}
                title={t('ticket.creationEvent.localization.modal.title')}
                submitButton={
                    <Button primary onClick={handleSubmit} disabled={isSubmittingFakeseating} isLoading={isSubmittingFakeseating} label={t('ticket.common.action.activate')} />
                }
                cancelButton={
                    <Button
                        onClick={() => {
                            resetFakeseatingState();
                        }}
                        label={t(`ticket.common.action.back`)}
                    />
                }
            >
                {t('ticket.creationEvent.localization.modal.text')}
            </Modal>
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.creationEvent.localization.formLabelType')} />
                <div>
                    <RadioLabel
                        className={Styles['radio-label-custom']}
                        inline
                        bordered
                        name="location"
                        checked={formEvent?.localization_type === 'location'}
                        label={t('ticket.creationEvent.localization.formLabelTypeLocation')}
                        onClick={() => {
                            onChangeObject({
                                ...{ localization_type: 'location' },
                                ...resetForm
                            });
                            track('ACTION > SELECT PLACE TYPE', {
                                'Place type': 'Location',
                                'Page Title': 'CREATE EVENT'
                            });
                        }}
                    />
                    <RadioLabel
                        className={Styles['radio-label-custom']}
                        inline
                        bordered
                        name="online"
                        checked={formEvent?.localization_type === 'online'}
                        label={t('ticket.creationEvent.localization.formLabelTypeOnline')}
                        onClick={() => {
                            onChangeObject({
                                ...{ localization_type: 'online' },
                                ...resetForm
                            });
                            track('ACTION > SELECT PLACE TYPE', {
                                'Place type': 'Online',
                                'Page Title': 'CREATE EVENT'
                            });
                        }}
                    />
                    {['online_donations', 'memberships'].includes(formEvent.subtype) && (
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            name="no_location"
                            checked={formEvent?.localization_type === 'no_location'}
                            label={t('ticket.creationEvent.localization.formLabelTypeNoLocation')}
                            onClick={() => {
                                onChangeObject({
                                    ...{ localization_type: 'no_location' },
                                    ...resetForm
                                });
                                track('ACTION > SELECT PLACE TYPE', {
                                    'Place type': 'None',
                                    'Page Title': 'CREATE EVENT'
                                });
                            }}
                        />
                    )}
                </div>
            </Form.Field>
            {toggleFakeseating && permissions.hasCapacity('PyvarCapacity.ADMIN', organization.id) && permissions.hasCapacity('PyvarCapacity.DEV', organization.id) && (
                <Form.Field>
                    <ToggleLabel
                        label={<ToggleFakeseatingEvent />}
                        disabled={disableFakeseating || isSubmittingFakeseating}
                        inlinePosition="right"
                        onChange={(e, { checked }) => {
                            setFakeseating(checked);
                            setFakeseatingModal(true);
                        }}
                        checked={fakeseating}
                    />
                </Form.Field>
            )}
            {formEvent?.localization_type === 'location' && (
                <AddressForm formEvent={formEvent} onChange={onChangeObject} autoMode={autoMode} onAutoModeChange={setAutoMode} disabledAutoMode={disabledAutoMode} />
            )}
            {formEvent?.localization_type === 'online' && (
                <>
                    {!edit && (
                        <Form.Field>
                            <Form.Label label={t('ticket.creationEvent.localization.formLabelPlatform')} optionalText={t('ticket.common.labelOptional')} />
                            <Form.Select
                                value={formEvent?.communication_platform}
                                onChange={value => {
                                    onChange('communication_platform', value);
                                }}
                                options={platformOptions}
                                placeholder={t('ticket.common.action.select')}
                            />
                        </Form.Field>
                    )}
                    <Form.Field>
                        <Form.Label label={t('ticket.creationEvent.localization.formLabelWebsite')} />
                        <Form.Input
                            name="website"
                            placeholder={t('ticket.creationEvent.localization.formPlaceholderWebsite')}
                            value={formEvent?.website}
                            onChange={({ target: { value } }) => {
                                if (value.length >= 80) {
                                    // max length
                                    return;
                                }
                                onChange('website', value);
                            }}
                        />
                    </Form.Field>
                    {fieldErrors && (edit ? fieldErrors : <Form.Field>{fieldErrors}</Form.Field>)}
                </>
            )}
            <div className={Styles['buttons-submit']}>
                {onDone && (
                    <Button primary className={Styles['button-continue']} label={t('ticket.common.action.continue')} isLoading={isSubmitting} onClick={_onDone} disabled={false} />
                )}
                {children}
            </div>
        </>
    );
}

EventCreationLocalization.propTypes = {
    onChange: PropTypes.func.isRequired,
    onChangeObject: PropTypes.func.isRequired,
    onDone: PropTypes.func,
    onCardDone: PropTypes.func,
    formEvent: PropTypes.object.isRequired,
    platformOptions: PropTypes.array,
    autoMode: PropTypes.bool.isRequired,
    setAutoMode: PropTypes.func,
    toggleFakeseating: PropTypes.bool,
    disabledAutoMode: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    children: PropTypes.node,
    edit: PropTypes.bool
};
